// Single source of truth for the tickets loaders. Bound as
// element methods (`el.call('pageColumn', colKey)` /
// `el.call('reloadTickets')`) plus exported as plain functions for
// page `onCreate` to call directly.
//
// All ticket queries flow through `sdk.tickets.*` (Mongo-backed):
//   • tickets         — sdk.tickets.list/get/create/update
//   • ticket_columns  — sdk.tickets.columns.list  (was /sb/rest/v1/ticket_columns)
//   • epic counts     — sdk.tickets.epicCounts    (was /sb/rest/v1/ticket_epic_counts view)
//
// (The former project_fields Supabase REST holdout was removed — its only
//  reader, fetchProjectFieldOptions, was dead; the table is dropped in 0159.)

// getSDK comes from @symbo-ls/workspace-shared, stubbed by frank's
// EXTERNAL_PACKAGES at serialization time — reached via el.call (or
// this.call, for the two functions below already designed for
// el.call('name') binding) instead of a raw import — see
// TicketDetailModal.js for the full rationale. The six functions below
// that returned data directly now take `el` first; pageColumn /
// reloadTickets / loadEpicsTree already bind `this` via el.call and pass
// it straight through.
import {
  BOARD_PLANNING_TYPES,
  personFilterEmails,
  countTicketsInColumn,
  ticketsEpicNames
} from './ticketUtils.js'

export const PAGE_SIZE = 16 // per-column page size (per Nika #6 — limit 16)
export const INITIAL_PAGE_SIZE = 16

// Standard workflow columns — lets the board fire first-page fetches in
// parallel with the counts aggregation instead of waiting for totals to
// learn which columns exist (saves a full request wave on boot).
// 'unsynced' is retired from the board (per Nika 2026-06-10).
// 'blocked' is a standard board column: on a cold boot the load wave often
// fires before root.columns lands, and a rendered column whose key this
// list misses never receives a loadedByColumn number — its spinner never
// clears (the BLOCKED endless-loading loop, 2026-09-02). board.js also
// self-heals any remaining miss after totals land.
export const DEFAULT_COLUMN_KEYS = [
  'backlog',
  'todo',
  'in_progress',
  'blocked',
  'ready_to_test',
  'review',
  'done'
]

// Normalize a Mongo ticket doc to the flat shape expected by board components.
// Dual-reads camelCase||snake_case fields to handle any API response shape in
// transition. GitHub-mirror-only fields (github_issue_url, github_node_id,
// number, content_type, repository, milestone, iteration_*) are zeroed/nulled
// — they were never migrated to Mongo.
// Legacy GitHub-Projects GUID column ids → semantic state keys. Older
// tickets persisted the GUID as columnKey; normalize at load so My View
// bucketing (isActiveColumn) + status display work (#2321 — same GUID-vs-
// semantic family as #2290).
const _GUID_TO_STATE = {
  '4d6ab783': 'in_progress',
  '821cd85d': 'review',
  '20f135e3': 'ready_to_test',
  d677f7ee: 'ready_to_test',
  eede5560: 'done'
}
const _normCol = (c) => _GUID_TO_STATE[c] || c || null

const _mongoToTicket = (doc, isPartial = false) => {
  const t = {
    id: doc._id || doc.id,
    key: String(doc._id || doc.id)
  }

  if (!isPartial || 'title' in doc) t.title = doc.title || ''
  if (!isPartial || 'body' in doc) t.body = doc.body || ''
  if (!isPartial || 'columnKey' in doc || 'column_key' in doc) {
    t.columnKey = _normCol(doc.columnKey || doc.column_key)
    t.column_key = _normCol(doc.columnKey || doc.column_key)
  }
  if (!isPartial || 'position' in doc) t.position = doc.position || 0
  if (!isPartial || 'workspace' in doc || 'workspaceId' in doc || 'workspace_id' in doc) {
    t.workspaceId = doc.workspace || doc.workspaceId || doc.workspace_id || null
  }
  if (!isPartial || 'assigneeEmail' in doc || 'assignee_email' in doc || 'assignees' in doc) {
    const email = (() => {
      let e = doc.assigneeEmail || doc.assignee_email
      if (!e && Array.isArray(doc.assignees) && doc.assignees.length > 0) {
        e = doc.assignees
          .map((a) => a.email)
          .filter(Boolean)
          .join(', ')
      }
      return e || null
    })()
    t.assigneeEmail = email
    t.assignee_email = email
    t.assignees = doc.assignees || []
  }
  if (!isPartial || 'assigneeLogins' in doc) t.assigneeLogins = doc.assigneeLogins || []
  if (!isPartial || 'reporterEmail' in doc || 'reporter_email' in doc) {
    t.reporterEmail = doc.reporterEmail || doc.reporter_email || null
    t.reporter_email = doc.reporterEmail || doc.reporter_email || null
  }
  if (!isPartial || 'labels' in doc) t.labels = doc.labels || []
  if (!isPartial || 'priority' in doc) t.priority = doc.priority || null
  if (!isPartial || 'state' in doc) t.state = doc.state || null
  if (!isPartial || 'type' in doc || 'issueType' in (doc.metadata || {})) {
    t.type = doc.type || null
    t.issueType = doc.type || doc.metadata?.issueType || null
  }

  if (!isPartial || 'externalId' in doc || 'external_id' in doc) {
    t.external_id = doc.externalId || doc.external_id || null
    t.externalId = doc.externalId || doc.external_id || null
  }

  if (!isPartial || 'env' in doc || 'env' in (doc.metadata || {})) {
    t.env = doc.env || doc.metadata?.env || null
  }
  if (!isPartial || 'metadata' in doc) t.metadata = doc.metadata || {}
  if (!isPartial || 'updatedAt' in doc || 'updated_at' in doc) {
    t.updatedAt = doc.updatedAt || doc.updated_at || null
    t.updated_at = doc.updatedAt || doc.updated_at || null
  }
  if (!isPartial || 'createdAt' in doc || 'created_at' in doc) {
    t.createdAt = doc.createdAt || doc.created_at || null
    t.created_at = doc.createdAt || doc.created_at || null
  }
  if (!isPartial || 'dueDate' in doc || 'due_date' in doc) {
    t.dueDate = doc.dueDate || doc.due_date || null
  }
  // The task↔project link (Ticket.project) — the board's ?project= scope
  // reads it on a realtime row (ticketUtils ticketBelongsOnBoard).
  if (!isPartial || 'project' in doc) t.project = doc.project ? String(doc.project) : null
  if (!isPartial || 'epic' in doc || 'epicName' in doc || 'epic_name' in doc) {
    t.epicName = doc.epic || doc.epicName || doc.epic_name || null
    t.epic_name = doc.epic || doc.epicName || doc.epic_name || null
  }
  if (!isPartial || doc.metadata) {
    const meta = doc.metadata || {}
    if (!isPartial || 'severity' in meta) t.severity = meta.severity || null
    if (!isPartial || 'storyPoints' in meta) t.storyPoints = meta.storyPoints || null
    if (!isPartial || 'acceptanceCriteria' in meta)
      t.acceptanceCriteria = meta.acceptanceCriteria || null
    if (!isPartial || 'reproSteps' in meta || 'stepsToReproduce' in meta) {
      t.stepsToReproduce = meta.reproSteps || meta.stepsToReproduce || null
    }
    if (!isPartial || 'expectedResult' in meta) t.expectedResult = meta.expectedResult || null
  }
  if (!isPartial) {
    t.sub_issues_total = 0
    t.sub_issues_completed = 0
    t.subIssuesTotal = 0
    t.subIssuesCompleted = 0
    t.githubUrl = null
    t.githubNodeId = null
    t.number = null
    t.contentType = null
    t.repository = null
    t.milestone = null
    t.iterationId = null
    t.iterationTitle = null
    t.iterationStart = null
    t.iterationEnd = null
    t.trackedByCount = 0
    t.tracksCount = 0
    t.issueTypeColor = null
    t.epicOptionId = null
    t.parentIssueUrl = null
  }

  return t
}

// Public alias for _mongoToTicket — writers that receive a raw server doc
// back from a PUT/POST (moveTicket / saveTicket's update branch) normalize
// it through the SAME function every read path uses, so a row landed via a
// write response is byte-identical in shape to one landed via a fetch. This
// is the single normalizer; do not hand-roll a second doc→ticket mapper.
export const normalizeTicketDoc = _mongoToTicket

// Build a Mongo filter from the column-filter options used by the board.
// workspace_id scope is applied server-side from JWT — do NOT pass it here.
const _buildMongoFilter = (filters = {}) => {
  const f = { deleted: false }
  // Multi-select person picks arrive as ARRAYS of emails (sidebar pickers,
  // 2026-07-25 per Nika "allow multiple selects in assignee and assigned
  // filters"); single strings / categories keep their legacy handling.
  if (Array.isArray(filters.assignee)) {
    if (filters.assignee.length === 1) f.assigneeEmail = filters.assignee[0]
    else if (filters.assignee.length > 1) f.assigneeEmail = { $in: filters.assignee }
  } else if (filters.assignee && filters.assignee !== 'all') {
    if (filters.assignee === 'me' && filters.currentUserEmail) {
      f.assigneeEmail = filters.currentUserEmail
    } else if (filters.assignee === 'agents') {
      f.assigneeEmail = { $regex: '.*-agent@symbols\\.app$' }
    } else if (filters.assignee === 'team') {
      f.assigneeEmail = { $not: { $regex: '.*-agent@symbols\\.app$' } }
      if (filters.currentUserEmail) f._assigneeNot = filters.currentUserEmail
    } else {
      f.assigneeEmail = filters.assignee
    }
  }
  // Issuer = the ticket's reporter (creator). One or more emails filter the
  // board to tickets those people raised. `reporterEmail` is already a
  // first-class Mongo field on tickets (see _mongoToTicket + fetchTicketsForUser),
  // so this needs no server change. (2026-06-15 per Nika: TicketsSidebar Issuer.)
  if (Array.isArray(filters.issuer)) {
    if (filters.issuer.length === 1) f.reporterEmail = filters.issuer[0]
    else if (filters.issuer.length > 1) f.reporterEmail = { $in: filters.issuer }
  } else if (filters.issuer && filters.issuer !== 'all') {
    f.reporterEmail = filters.issuer
  }
  if (filters.search) {
    const q = filters.search.replace(/[%*]/g, '').trim()
    if (q) f.title = { $regex: q, $options: 'i' }
  }
  // Epic filter is NAME-based on the first-class Mongo `epic` column (the
  // legacy epicOptionId was a GitHub-Projects GUID that Mongo rows never
  // carry — filtering on it silently matched nothing). Set by the sidebar
  // EPIC picker (2026-07-25 per Nika "add epics in filter sidebar").
  if (filters.epic) f.epic = filters.epic
  if (filters.issueType) f.type = filters.issueType
  // Epics + user stories are PLANNING items with their own surface
  // (/tickets/epics) — they don't belong on the work board as cards
  // (2026-06-10 per Nika "stories and epics should not be listed as
  // tickets/tasks"). Excluded by default; the TYPE filter's explicit
  // Epics/Stories picks still show them (issueType wins above). ONE list,
  // shared with the client-side twin `ticketBelongsOnBoard`.
  else f.type = { $nin: BOARD_PLANNING_TYPES }
  // Roadmap-cycle scoping. /tickets/board?cycle=<id> sets root.filterCycleId
  // on mount; filtersFromRoot threads it through every column fetch.
  if (filters.cycleId) f.cycleId = filters.cycleId
  // The task↔project link (Ticket.project, the project's id): /tasks?project=
  // and the AI-answer view's `project` (the project the ask names, resolved
  // to its id). The server checks the project belongs to the workspace.
  if (filters.project) f.project = filters.project
  // Priority (P0–P3) — the AI-answer view's `priority`, one place for the
  // list read, the column pages and the column counts alike.
  if (filters.priority) f.priority = filters.priority
  // Tickets are workspace-scoped server-side (workspaceId required since
  // server@ba9e0d8f). Pass the board's workspace explicitly; the SDK also
  // falls back to its active-workspace context when absent.
  if (filters.workspaceId) f.workspaceId = filters.workspaceId
  return f
}

// Phase 7c-bis: fetchColumnPage → sdk.tickets.list.
// FIELD_EMBED (ticket_field_values join) is dropped — Mongo docs carry
// custom fields in metadata; board cards only need core fields.
export const fetchColumnPage = async (el, colKey, offset, limit, filters = {}, src = '?') => {
  // Frank's serializer only classifies bare identifiers referenced inside
  // FUNCTION BODIES — an identifier used as a DEFAULT-PARAMETER expression
  // is outside that scan and comes back "resolves to nothing at runtime"
  // once this function is emitted on its own. Default inside the body
  // instead so `PAGE_SIZE` is a plain body read (like the body-only usage a
  // few lines below in `pageColumn`, which already resolves cleanly).
  if (limit == null) limit = PAGE_SIZE
  try {
    const tSvc = await el.call('getSDK').getService('tickets')
    const filter = { ..._buildMongoFilter(filters), columnKey: colKey }
    const result = await tSvc.list(filter, {
      sortBy: 'position',
      sortDir: 'asc',
      limit,
      skip: offset
    })
    const docs = Array.isArray(result) ? result : result.data || []
    return docs.map(_mongoToTicket)
  } catch (err) {
    console.warn(
      '[ticketLoaders] page fetch failed for',
      colKey,
      'src:',
      src,
      'ws:',
      filters?.workspaceId || 'NONE',
      err?.message || err
    )
    return []
  }
}

// The tickets an AI ANSWER shows (functions/ticketsAnswer.js — the
// `tickets-board` view): the board's own filter (_buildMongoFilter: assignee,
// issuer, type, search, epic, cycle, project, priority, the workspace) + a
// status:
//   open   every workflow column but done — never a draft, a task outside
//          every column (QA on dev: "My open tasks" counted the Draft lane as
//          open); `drafts: true` adds the drafts back
//   all    every task, done and drafts included
//   draft  the drafts alone
//   <key>  one board column
// Newest change first, at most 50; `total` is how many match (the server's
// count), so the view can say "20 of 216".
//
// Two filters the server cannot apply run here, over bounded pages of 100
// (five at most): a DUE window (`due: { from, to, none, open }`, days as
// YYYY-MM-DD — the day a due date names) reads by due date, latest first, and
// stops once a page passes the window; PLACED (`placed: true`, the canvas
// layout) keeps the tasks the /tasks board places on its canvas
// (ticketCanvasPos). Their `total` is set only when the pages reached the end.
//
// The server scopes the read to the member's workspace and grants. [] on a
// failed read, with `failed: true` so the view says so instead of "no tasks".
export const fetchTicketsForAnswer = async (el, filters = {}, limit = 20) => {
  const cap = Math.min(Math.max(Number(limit) || 20, 1), 50)
  try {
    const tSvc = await el.call('getSDK').getService('tickets')
    const filter = _buildMongoFilter(filters)
    const status = filters.status || 'open'
    if (status === 'open') {
      filter.columnKey = filters.drafts === true ? { $ne: 'done' } : { $nin: ['done', 'draft'] }
    } else if (status !== 'all') filter.columnKey = status
    const rowsOf = (res) => (Array.isArray(res) ? res : (res && res.data) || [])
    const countOf = (res) => {
      const n = res && !Array.isArray(res) ? Number(res.count) : NaN
      return Number.isFinite(n) ? n : null
    }
    const due = filters.due && typeof filters.due === 'object' ? filters.due : null
    const placed = filters.placed === true
    if (!due && !placed) {
      const result = await tSvc.list(filter, { sortBy: 'updatedAt', sortDir: 'desc', limit: cap })
      const out = rowsOf(result).map((d) => _mongoToTicket(d))
      const total = countOf(result)
      if (total !== null) out.total = total
      return out
    }
    // A dated window reads by due date, latest first — a task with no date
    // sorts last — so once a page reaches a day before the window, or a task
    // with no date, nothing after it can match.
    const dated = !!(due && !due.none)
    const dayOf = (t) => (t && t.dueDate ? String(t.dueDate).slice(0, 10) : '')
    const root = (el && el.state && el.state.root) || null
    const keep = (t) => {
      if (due) {
        const day = dayOf(t)
        if (due.none) {
          if (day) return false
        } else {
          if (!day) return false
          if (due.from && day < due.from) return false
          if (due.to && day > due.to) return false
          if (due.open && t.columnKey === 'done') return false
        }
      }
      return !placed || !!el.call('ticketCanvasPos', root, t)
    }
    const PAGE = 100
    const PAGES = 5
    const found = []
    let skip = 0
    let complete = false
    for (let page = 0; page < PAGES; page++) {
      const result = await tSvc.list(filter, {
        sortBy: dated ? 'dueDate' : 'updatedAt',
        sortDir: 'desc',
        limit: PAGE,
        skip
      })
      const rows = rowsOf(result).map((d) => _mongoToTicket(d))
      for (const t of rows) if (keep(t)) found.push(t)
      const last = rows[rows.length - 1]
      const more =
        result && typeof result.hasMore === 'boolean' ? result.hasMore : rows.length === PAGE
      const passed = dated && !!last && (!dayOf(last) || (!!due.from && dayOf(last) < due.from))
      if (!rows.length || !more || passed) {
        complete = true
        break
      }
      if (!dated && found.length >= cap) break
      skip += rows.length
    }
    // A due window lists the nearest day first (the most overdue first).
    if (dated) found.sort((a, b) => (dayOf(a) < dayOf(b) ? -1 : dayOf(a) > dayOf(b) ? 1 : 0))
    const out = found.slice(0, cap)
    if (complete) {
      // Every match was read: the full count, and per board column.
      out.total = found.length
      out.byColumn = {}
      for (const t of found) {
        const key = t.columnKey || 'backlog'
        out.byColumn[key] = (out.byColumn[key] || 0) + 1
      }
    }
    return out
  } catch (err) {
    console.warn('[ticketLoaders] answer fetch failed:', err?.message || err)
    const out = []
    out.failed = true
    return out
  }
}

// Lightweight feed of recently-updated tickets — used by the General
// and My View pages so non-engineering stakeholders see fresh activity
// without paying for the full board's per-column pagination.
// Phase 7c-bis: → sdk.tickets.list.
export const fetchRecentTickets = async (el, limit = 20) => {
  try {
    const tSvc = await el.call('getSDK').getService('tickets')
    const result = await tSvc.list(
      { deleted: false },
      { sortBy: 'updatedAt', sortDir: 'desc', limit }
    )
    const docs = Array.isArray(result) ? result : result.data || []
    return docs.map(_mongoToTicket)
  } catch (_) {
    return []
  }
}

// Tickets assigned to a single user — drives the My View page.
// Phase 7c-bis: → sdk.tickets.list.
// Default limit bumped to 100 (from 50) so ASK-USER / awaiting-approval
// items near the end of a large queue are not silently truncated.
export const fetchTicketsAssignedTo = async (el, email, limit = 100) => {
  if (!email) return []
  try {
    const tSvc = await el.call('getSDK').getService('tickets')
    const result = await tSvc.list(
      { assigneeEmail: email, deleted: false },
      { sortBy: 'updatedAt', sortDir: 'desc', limit }
    )
    const docs = Array.isArray(result) ? result : result.data || []
    return docs.map(_mongoToTicket)
  } catch (_) {
    return []
  }
}

// Tickets relevant to a single user — those ASSIGNED TO them plus those
// they REPORTED (created). Drives the home dashboard's "Received tasks"
// (assigned) and "Given tasks" (reported) widgets, which read
// `root.tickets`. Without this the widgets only had data after the user
// had visited a /tickets board (which is what populated root.tickets);
// on a cold home load both rendered empty. Returns a merged, de-duped,
// _mongoToTicket-normalized array.
// `throwOnError` (FALSE-ZERO-HOME-NON-AGENT-KEYS-1): by default every
// failure collapses to `[]`, which a consumer cannot tell from a verified
// empty list. A caller that records load state opts in and gets the error.
export const fetchTicketsForUser = async (
  el,
  email,
  limit = 100,
  { throwOnError = false } = {}
) => {
  if (!email) return []
  try {
    const tSvc = await el.call('getSDK').getService('tickets')
    const [assignedRes, reportedRes] = await Promise.all([
      tSvc
        .list(
          { assigneeEmail: email, deleted: false },
          { sortBy: 'updatedAt', sortDir: 'desc', limit }
        )
        .catch((err) => {
          if (throwOnError) throw err
          return []
        }),
      tSvc
        .list(
          { reporterEmail: email, deleted: false },
          { sortBy: 'updatedAt', sortDir: 'desc', limit }
        )
        .catch((err) => {
          if (throwOnError) throw err
          return []
        })
    ])
    const assigned = Array.isArray(assignedRes) ? assignedRes : assignedRes?.data || []
    const reported = Array.isArray(reportedRes) ? reportedRes : reportedRes?.data || []
    const byId = new Map()
    for (const doc of [...assigned, ...reported]) {
      const row = _mongoToTicket(doc)
      if (row.id) byId.set(row.id, row)
    }
    return Array.from(byId.values())
  } catch (err) {
    if (throwOnError) throw err
    return []
  }
}

// fetchProjectFieldOptions removed — dead export with zero callers; it was the
// last reader of the project_fields table, dropped in migration 0159. The
// SUPABASE_URL/restHeaders import above is removed in the same change since
// nothing else in this file used it.

// Per-epic count + per-column breakdown for the Epics index. Returns
// `{ epicName: { total, byColumn } }`. Prefers the server-side aggregation
// at sdk.tickets.epicCounts (one round-trip). Falls back to sdk.tickets.list
// aggregation when the SDK route is unavailable.
export const fetchEpicCounts = async (el, epicNames) => {
  const out = {}
  for (const name of epicNames || []) out[name] = { total: 0, byColumn: {} }

  // Fast path: SDK-aggregated counts (Mongo-backed).
  try {
    const tSvc = await el.call('getSDK').getService('tickets')
    const result = await tSvc.epicCounts()
    const rows = Array.isArray(result) ? result : result?.data || []
    if (rows.length) {
      for (const r of rows) {
        const epicName = r.epic_name || r.epicName
        if (!epicName) continue
        out[epicName] = {
          total: r.total || 0,
          byColumn: r.by_column || r.byColumn || {}
        }
      }
      return out
    }
  } catch (_) {
    // fall through to client-side aggregation
  }

  // Fallback: aggregate client-side via sdk.tickets.listAll (list() caps at
  // 500 server-side and never read `sort` —
  // WS-TICKET-READS-SILENTLY-CAPPED-AT-500-1).
  try {
    const tSvc = await el.call('getSDK').getService('tickets')
    const page = await tSvc.listAll(
      { deleted: false, epic: { $ne: null } },
      { sortBy: 'updatedAt', sortDir: 'desc' }
    )
    const tickets = Array.isArray(page?.items) ? page.items : []
    for (const t of tickets) {
      const epicName = t.epic || t.epicName || t.epic_name
      if (!epicName) continue
      if (!out[epicName]) out[epicName] = { total: 0, byColumn: {} }
      out[epicName].total += 1
      const colKey = t.columnKey || t.column_key
      if (colKey) {
        out[epicName].byColumn[colKey] = (out[epicName].byColumn[colKey] || 0) + 1
      }
    }
  } catch (_) {}
  return out
}

// Per-column row counts. Fast path: ONE grouped server aggregation
// (sdk.tickets.columnCounts → POST /tickets/column-counts) — replaces the
// former per-column list({limit:0, includeCount}) fan-out (7 countDocuments
// per board load AND per 30s poll tick, a large share of the board's load
// time). Falls back to the legacy fan-out when the SDK/server pair predates
// the endpoint. `{ strict: true }` (the AI-answer board — functions/
// ticketsAnswer.js) answers null instead of the fallback: a count it cannot
// read is unknown there, never a 0 on a column that holds tasks.
export const fetchColumnCounts = async (el, filters = {}, colKeys = null, opts = {}) => {
  const strict = !!(opts && opts.strict)
  let keys = colKeys
  if (!keys) {
    try {
      const tSvc = await el.call('getSDK').getService('tickets')
      const cols = await tSvc.columns.list()
      const rows = Array.isArray(cols) ? cols : cols?.data || []
      keys = rows.map((c) => c.key).filter(Boolean)
    } catch (err) {
      console.warn('[ticketLoaders] columns.list failed:', err?.message || err)
      keys = []
    }
  }
  const mongoFilter = _buildMongoFilter(filters)
  try {
    const tSvc = await el.call('getSDK').getService('tickets')
    if (typeof tSvc.columnCounts === 'function') {
      const grouped = await tSvc.columnCounts(mongoFilter)
      if (grouped && typeof grouped === 'object' && !Array.isArray(grouped)) {
        // Zero-fill the known column keys (the aggregation omits empty
        // columns) and keep any extra keys it returned.
        const out = {}
        for (const key of keys) out[key] = grouped[key] || 0
        for (const k of Object.keys(grouped)) if (!(k in out)) out[k] = grouped[k]
        return out
      }
    }
  } catch (err) {
    console.warn(
      '[ticketLoaders] columnCounts fast path failed, falling back:',
      err?.message || err
    )
  }
  if (strict) return null
  // Legacy fan-out — one count query per column.
  const out = {}
  try {
    const tSvc = await el.call('getSDK').getService('tickets')
    await Promise.all(
      keys.map(async (key) => {
        try {
          const result = await tSvc.list(
            { ...mongoFilter, columnKey: key },
            { limit: 0, includeCount: true }
          )
          // SDK may return { data, count } or plain array (older SDK).
          out[key] =
            typeof result?.count === 'number'
              ? result.count
              : Array.isArray(result)
                ? result.length
                : 0
        } catch (err) {
          console.warn('[ticketLoaders] count failed for', key, err?.message || err)
          out[key] = 0
        }
      })
    )
  } catch (err) {
    console.warn('[ticketLoaders] count fan-out failed:', err?.message || err)
    for (const key of keys) out[key] = 0
  }
  return out
}

// The sidebar TYPE filter's buckets — the `value`s of components/Sidebar.js
// TicketsTypeGroup and the keys of root.totalByType. 'all' counts under the
// board's default type rule (planning types excluded — _buildMongoFilter).
export const TICKET_TYPE_COUNT_BUCKETS = ['all', 'bug', 'task', 'user_story']

// Per-type ticket counts for the sidebar TYPE filter (PR #283), landed in
// root.totalByType. That key is declared beside totalByColumn in
// tickets/state.js and the shell's state-app.js, and it is WORKSPACE_SCOPED:
// a workspace switch wipes it and the board's next wave refills it (the
// root-write census went red on it as an undeclared first-write key).
//
// A COUNT read, never a page read. Each bucket is ONE grouped aggregation on
// the endpoint the column totals already use (sdk.tickets.columnCounts),
// summed over its columns — no ticket document crosses the wire. The first
// version asked tSvc.list({ limit: 0, includeCount: true }) per bucket; the
// server reads `limit: 0` as its default page, so every "count" downloaded up
// to 100 ticket documents on each load, poll tick and realtime refresh, and
// pages/board.test.js read the four extra list() calls as a second page wave.
//
// Deduplicated against the column totals: the bucket whose filter IS the
// board's own filter ('all' with no TYPE filter, else the picked type) reuses
// `boardTotals` — the caller's in-flight fetchColumnCounts(el, filters)
// promise for the SAME filters — instead of asking the server twice. The
// server has no type-grouped count, so one aggregation per other bucket is
// the floor without a server change.
//
// The active TYPE filter is ignored: each bucket counts under every OTHER
// filter. A bucket whose read fails is left out, so its label shows no number
// instead of a false 0.
//
// A READ, never a write: it answers `{ totals, raw }` — the per-bucket sums
// (hidden and excluded columns left out) and PR #287's raw per-bucket x
// per-column counts behind them — and the CALLER lands both in its own
// root.update (the board's load wave, refreshTicketsCounts below). It used to
// assign `el.state.root._rawTypeCounts` directly: a silent root write the
// board never repainted for.
export const fetchTypeCounts = async (el, filters = {}, boardTotals = null) => {
  const out = {}
  const rawGrouped = {}
  const boardBucket = filters.issueType || 'all'
  const readBucket = async (bucket) => {
    if (boardTotals && bucket === boardBucket) return boardTotals
    const tSvc = await el.call('getSDK').getService('tickets')
    return tSvc.columnCounts(
      _buildMongoFilter({ ...filters, issueType: bucket === 'all' ? null : bucket })
    )
  }
  await Promise.all(
    TICKET_TYPE_COUNT_BUCKETS.map(async (bucket) => {
      try {
        const grouped = await readBucket(bucket)
        if (grouped && typeof grouped === 'object' && !Array.isArray(grouped)) {
          rawGrouped[bucket] = grouped
          // `el.state` is not assumed: an element without it (a teardown, a
          // harness) read `el.state.root` here and failed EVERY bucket — the
          // sidebar showed no counts and the board's tests logged "type count
          // failed … reading 'root'" on every read (2026-09-26 → 09-29).
          const stateRoot = (el && el.state && el.state.root) || null
          const excludedCols = (stateRoot && stateRoot._excludedCols) || {}
          const hiddenCols = (stateRoot && stateRoot._hiddenCols) || {}
          out[bucket] = Object.entries(grouped).reduce((sum, [colKey, v]) => {
            if (excludedCols[colKey] || hiddenCols[colKey]) return sum
            return sum + (Number(v) || 0)
          }, 0)
        }
      } catch (err) {
        console.warn('[ticketLoaders] type count failed for', bucket, err?.message || err)
      }
    })
  )
  return { totals: out, raw: rawGrouped }
}

// THE counts refresh of the /tickets board — a registered project function:
//   el.call('refreshTicketsCounts')
// from the board's realtime channel (a ticket moved, created or deleted), its
// 30 s poll, the Task Settings modal and the kanban's column menu. `this` is
// the calling element; the root comes from `this.state.root`.
//
// It replaces PR #287's `root._refreshTicketsCounts` — a FUNCTION stored on
// root state by the board's onCreate, pointing at a lowercase function prop of
// the page that the framework auto-invokes on read (Rule 70), so the call
// never refreshed anything. Root state keeps DATA only: the counts
// (totalByColumn, totalByType, _rawTypeCounts) and the board's status
// `_ticketsBoardLive`.
//
// Each call re-reads the column keys and the filters off root, in the load
// wave's own shape (filtersFromRoot): keys captured once went stale while the
// columns hydrated (the stale 15/15/15 header counts), and a filter copy that
// lacked `issuer` once showed the unfiltered total.
//
// One read, at most one repaint: both reads, then ONE root.update carrying
// only the keys whose numbers changed — nothing when none did. Off the board
// (the status is not live) it reads nothing. An answer for a query the board
// has left — a filter changed, the workspace switched, the board unmounted
// while the reads were out — is dropped: the board's own reload owns the new
// query, and a late answer must never paint the previous query's counts.
// Resolves true when it repainted.
export const refreshTicketsCounts = async function refreshTicketsCounts() {
  const el = this
  const root = el && el.state ? el.state.root || el.state : null
  if (!root || typeof root.update !== 'function') return false
  if (root._ticketsBoardLive !== true) return false
  const queryOf = (f) =>
    JSON.stringify([
      f.search,
      f.assignee,
      f.issuer,
      f.epic,
      f.issueType,
      f.cycleId,
      f.project,
      f.workspaceId
    ])
  const same = (a, b) => {
    const x = a && typeof a === 'object' ? a : {}
    const y = b && typeof b === 'object' ? b : {}
    const keys = Object.keys(x)
    if (keys.length !== Object.keys(y).length) return false
    return keys.every((k) => (x[k] && typeof x[k] === 'object' ? same(x[k], y[k]) : x[k] === y[k]))
  }
  const filters = filtersFromRoot(root)
  const asked = queryOf(filters)
  const colKeys = (root.columns || []).map((c) => c.key)
  let cols
  let types
  try {
    const colsPromise = fetchColumnCounts(el, filters, colKeys.length ? colKeys : null)
    const answers = await Promise.all([colsPromise, fetchTypeCounts(el, filters, colsPromise)])
    cols = answers[0]
    types = answers[1]
  } catch (err) {
    console.warn('[ticketLoaders] counts refresh failed:', err?.message || err)
    return false
  }
  if (root._ticketsBoardLive !== true || queryOf(filtersFromRoot(root)) !== asked) return false
  const patch = {}
  if (!same(cols, root.totalByColumn)) patch.totalByColumn = cols
  if (!same(types.totals, root.totalByType)) patch.totalByType = types.totals
  if (!same(types.raw, root._rawTypeCounts)) patch._rawTypeCounts = types.raw
  if (!Object.keys(patch).length) return false
  root.update(patch)
  return true
}

const filtersFromRoot = (root) => ({
  search: root?.searchQuery || '',
  assignee: root?.filterAssignee || 'all',
  issuer: root?.filterIssuer || 'all',
  epic: root?.filterEpic || null,
  issueType: root?.filterIssueType || null,
  cycleId: root?.filterCycleId || null,
  project: root?.filterProjectId || null,
  currentUserEmail: root?.currentUserEmail,
  workspaceId: root?.workspace?.id || null
})

// actionFn for the shared ContextDropdown when a TicketsSidebar person
// picker (Assignee / Issuer) row is chosen. `action` = { field, value, multi }.
// Multi rows TOGGLE the email in the filter array and keep the menu open (so
// several people can be picked in one visit — 2026-07-25 per Nika "allow
// multiple selects in assignee and assigned filters"); "Anyone" clears. The
// board's subscribeTo watcher refetches on the field change.
export function applyTicketsFilterPick(action) {
  const root = this?.state?.root
  if (!root || !action || !action.field) return
  if (action.multi) {
    const cur = personFilterEmails(root[action.field])
    let next = null
    if (action.value) {
      next = cur.includes(action.value)
        ? cur.filter((e) => e !== action.value)
        : [...cur, action.value]
      if (!next.length) next = null
    }
    // Re-write the dropdown descriptor with a fresh identity so the menu's
    // children re-run itemsFn and the check marks update in place.
    const dd = root.contextDropdown
    root.update({ [action.field]: next, contextDropdown: dd ? { ...dd } : dd })
    return
  }
  root.update({ [action.field]: action.value || null, contextDropdown: null })
}

// Email-bearing AGENTS from the shared roster (root._agents — filled by
// /team-and-agents' richer seed-merged loader when that page has run, or by
// ensureTicketsAgentsRoster below on tickets surfaces). Agents are Mongo
// Users, so their emails flow through the SAME assignee_email model as
// humans — assignability needs no server change, only listing them
// (2026-08-06 per Nika "agent should be able to assigned").
export const ticketsAgentAssignables = (root) =>
  (Array.isArray(root?._agents) ? root._agents : [])
    .filter((a) => a && a.email)
    .map((a) => ({
      name: a.name || a.id || a.email,
      email: a.email,
      avatar: a.avatar || '',
      kind: 'agent'
    }))

// people ∪ agents in one person-shaped pool — for every surface that
// resolves an assignee email to a display name/avatar (modal autocomplete,
// chips, card avatar stacks). Humans first so name collisions resolve to
// the human.
export const ticketsAssignablePool = (root) => [
  ...(root?.people || []).filter((p) => p && p.email),
  ...ticketsAgentAssignables(root)
]

// One-shot roster fill for tickets surfaces: root._agents is normally
// filled by /team-and-agents' own loader (canvas package) — which never
// runs when the user goes straight to /tasks. Fetch the raw roster here
// when the shared key is EMPTY, with a PRIVATE guard flag
// (_ticketsAgentsFetched, NOT the canvas loader's _agentsLoaded — claiming
// that would make /team-and-agents skip its richer seed-merged fill; since
// that fill doesn't check emptiness it freely overwrites this raw one
// later). Fire-and-forget from the picker/modal openers; when the fetch
// lands while a tickets people picker is open, the contextDropdown
// identity refresh re-runs itemsFn so the agent rows appear in place.
export async function ensureTicketsAgentsRoster() {
  const root = this?.state?.root
  const sdk = this?.sdk
  if (!root || root._ticketsAgentsFetched) return
  if (Array.isArray(root._agents) && root._agents.length) return
  if (!sdk || typeof sdk.listAgents !== 'function') return
  root.update({ _ticketsAgentsFetched: true }, { preventFetch: true })
  try {
    const orgId = root.org?.id || root.org?._id || null
    const raw = await sdk.listAgents(orgId)
    const agents = (Array.isArray(raw) ? raw : [])
      .map((u) => ({
        id: (u.metadata && u.metadata.seedSlug) || u.username || String(u._id || u.id || ''),
        userId: u._id || u.id || null,
        email: u.email || '',
        name: u.name || u.username || 'Agent',
        avatar: u.avatar || u.avatarUrl || '',
        kind: 'agent'
      }))
      .filter((a) => a.email)
    if (!agents.length) return
    if (Array.isArray(root._agents) && root._agents.length) return
    root.update({ _agents: agents }, { preventFetch: true })
    const dd = root.contextDropdown
    if (dd && dd.open && /^tickets(Assignee|Issuer)PickItems$/.test(dd.itemsFn || '')) {
      root.update({ contextDropdown: { ...dd } }, { preventFetch: true })
    }
  } catch (err) {
    console.warn('[tickets] agents roster fetch failed:', err?.message || err)
  }
}

// Reactive items builders for the multi-select person pickers — passed to the
// shared ContextDropdown as `itemsFn` (re-derived on every menu render, so the
// check marks track the live filter arrays). Humans first (sorted), then a
// separator line, then agents (sorted) — 2026-08-06 per Nika "in filter
// drdopwon make line and list agents below humans".
const _personPickItems = function (root, field) {
  const picked = personFilterEmails(root?.[field])
  const people = (root?.people || []).filter((p) => p && p.email)
  const agents = ticketsAgentAssignables(root)
  const rows = [
    {
      // A bundle KEY — the shared ContextDropdownItem resolves `s.label`
      // through polyglot, and every real person row below carries a name,
      // which polyglot returns verbatim.
      label: 'tickets.filter.anyone',
      icon: 'people',
      checked: !picked.length,
      action: { field, value: null, multi: true }
    },
    ...people
      .map((p) => ({
        label: p.name || p.username || p.email,
        avatar: this.call('avatarUrlFor', p.email),
        checked: picked.includes(p.email),
        action: { field, value: p.email, multi: true }
      }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
  ]
  if (agents.length) {
    rows.push({ key: `${field}-agents-divider`, divider: true })
    rows.push(
      ...agents
        .map((a) => ({
          label: a.name,
          avatar: a.avatar || this.call('avatarUrlFor', a.email),
          checked: picked.includes(a.email),
          action: { field, value: a.email, multi: true }
        }))
        .sort((x, y) => x.label.localeCompare(y.label, undefined, { sensitivity: 'base' }))
    )
  }
  return rows
}
export function ticketsAssigneePickItems(root) {
  return _personPickItems.call(this, root, 'filterAssignee')
}
export function ticketsIssuerPickItems(root) {
  return _personPickItems.call(this, root, 'filterIssuer')
}

// Epic rows for the sidebar EPIC picker — from ticketsEpicNames(root), the
// SAME merged (epicsTree ∪ ticket.epicName) list the modal's Epic select
// reads, so the two can never disagree about which epics are pickable.
// Single-select: value = the epic NAME (what Mongo's `epic` column stores
// and _mongoToTicket surfaces as epicName); no `multi` flag so
// applyTicketsFilterPick sets-and-closes.
export function ticketsEpicPickItems(root) {
  const active = root?.filterEpic || null
  const names = ticketsEpicNames(root)
  return [
    {
      label: 'tickets.filter.anyEpic',
      icon: 'star',
      checked: !active,
      action: { field: 'filterEpic', value: null }
    },
    ...names.map((name) => ({
      label: name,
      checked: active === name,
      // Toggle-clear: picking the active epic again clears the filter.
      action: { field: 'filterEpic', value: active === name ? null : name }
    }))
  ]
}

// Consolidated epic-TYPE ticket loader — replaces 3 near-identical inline
// fetches that used to live in Sidebar.js's TicketsEpicPicker.onClick,
// TicketDetailModal.js's onCreate, and TicketDetailModal.js's EditBtn.onClick
// (all did `svc.list({type:'epic'})` → map → `root.update({epicsTree})`,
// diverging slightly every time one was touched). ONE place now, callable
// via `el.call('loadEpicsTree')` from any of those sites plus the Manage
// Epics modal (which passes `force:true` after every create/rename/delete so
// the change is visible everywhere — filter, modal picker, manage list —
// without a reload).
//
// Stores FULL _mongoToTicket-normalized rows (not a thin {title,epicKey}
// projection) so the Manage Epics modal can re-save a row (rename) without
// first re-fetching it — spreading a thin projection into saveTicket would
// have blanked every field _toPayload sends unconditionally (body,
// assigneeEmail, labels, …). ticketsEpicNames + EpicField only ever read
// `.title`, so this is a superset, not a breaking shape change.
//
// Bound as an element method (`this.state?.root` / `this.sdk`, same contract
// as pageColumn/reloadTickets above) — el.call('loadEpicsTree') everywhere.
// Accepts an explicit `root` override for callers whose element context
// can't be trusted to inherit page state — e.g. a dropdown-portal menu item
// (pages/board.js's ticketsCreateRows: "the dropdown portal doesn't inherit
// page state", which is why every row there closes over `root` from the
// opener instead of reading its own element's state).
export const loadEpicsTree = async function ({ force = false, root: rootOverride } = {}) {
  const root = rootOverride || this.state?.root
  if (!root) return
  if (!force && root.epicsTree?.length) return
  // Prefer the live, init-deferring `this.sdk` over calling getSDK directly
  // — getSDK() can resolve null this early (mount-time onCreate), and its
  // swallowed throw used to commit an empty result that STUCK — epicsTree
  // looked "loaded" but stayed empty forever. this.sdk resolves once init
  // actually completes (mirrors the 3 call sites this replaces). This
  // function is `this`-bound (invoked via el.call('loadEpicsTree')), so
  // this.call reaches getSDK the same way el.call does elsewhere in this
  // file — no raw import either way.
  const sdk = this.sdk || this.call('getSDK')
  if (!sdk?.getService) return
  try {
    const svc = await sdk.getService('tickets')
    const page = await svc.listAll(
      { type: 'epic', deleted: false },
      { sortBy: 'title', sortDir: 'asc' }
    )
    const rows = Array.isArray(page?.items) ? page.items : []
    // Only commit a NON-EMPTY result unless force:true — a transient empty
    // response (auth not ready, brief network blip) must never poison an
    // already-populated cache. force:true (post-mutation refresh) always
    // commits, including empty — e.g. deleting the last epic must clear it.
    if (rows.length || force) {
      root.update({ epicsTree: rows.map(_mongoToTicket) })
    }
  } catch (err) {
    console.warn('[loadEpicsTree] failed:', err?.message || err)
  }
}

export const pageColumn = async function (colKey) {
  const root = this.state?.root
  if (!root) return
  // Offset derives from the rows ACTUALLY in the store, not the
  // loadedByColumn counter — the counter drifts (creates / drag-moves /
  // deletes mutate root.tickets without maintaining it), and a stale-low
  // offset re-fetched rows the column already displayed, appending
  // duplicate cards (2026-07-29 per Nika "after clicking duplicates").
  const loaded = countTicketsInColumn(root, colKey)
  const total = (root.totalByColumn || {})[colKey] || 0
  if (loaded >= total) return
  if ((root.loadingColumn || {})[colKey]) return
  root.update({ loadingColumn: { ...(root.loadingColumn || {}), [colKey]: true } })
  try {
    // fetchColumnPage already returns _mongoToTicket-normalized rows
    const rows = await fetchColumnPage(
      this,
      colKey,
      loaded,
      PAGE_SIZE,
      filtersFromRoot(root),
      'pageColumn'
    )
    // Dedupe by id no matter what the server returns — offset paging over
    // a mutating, position-sorted collection can always overlap (a ticket
    // moved into the column shifts every offset). Duplicates must be
    // impossible at the append site. String() both sides: optimistic
    // creates carry a numeric Date.now() id until the real id lands.
    const have = new Set((root.tickets || []).map((t) => String(t?.id)))
    const fresh = rows.filter((t) => t && !have.has(String(t.id)))
    const patch = {
      tickets: [...(root.tickets || []), ...fresh],
      loadedByColumn: { ...(root.loadedByColumn || {}), [colKey]: loaded + rows.length },
      loadingColumn: { ...(root.loadingColumn || {}), [colKey]: false }
    }
    // Self-heal a stale-high total: an empty page at this offset means the
    // server has no more rows — snap the total down so the More button
    // stops advertising pages that don't exist.
    if (!rows.length) {
      patch.totalByColumn = { ...(root.totalByColumn || {}), [colKey]: loaded }
    }
    root.update(patch)
  } catch (e) {
    console.error('[pageColumn]', e?.message || e)
    root.update({ loadingColumn: { ...(root.loadingColumn || {}), [colKey]: false } })
  }
}

// Plain-function form of `el.call('reloadTickets')`: the same
// column-paginated reload for a caller that holds the root state and an
// element. Keep `reloadTickets` below as the element-method wrapper so
// existing `el.call('reloadTickets')` callers don't change.
//
// `el` is the ELEMENT the reads go through: every read starts with
// `el.call('getSDK')` (the frank-safe call, see the header) and a state has
// no `call`. `reloadTickets` and the shell's switch tail used to hand the
// root STATE in as the element (since 819543f10): all three count reads
// failed ("el.call is not a function") and the reload then wrote empty counts
// and an empty board. With no element there is nothing to read through, so
// the reload says so once and leaves the board as it is.
//
// The writes go straight to the root, outside the board page's sequence
// guard, so each write first checks that the root still asks the query this
// wave read. A filter pick or a workspace switch while the reads were in
// flight makes these rows another query's (after a switch, another
// workspace's), and the newer load owns the board.
//
// Every column gets a loadedByColumn number: the counts' keys, the standard
// keys, and every key the board already held a number for (the board
// loader's drafts key and its healed columns among them), so no column's
// Loading block waits for a page this wave never asked for.
export const reloadTicketsForRoot = async (root, el) => {
  if (!root) return
  if (!el || typeof el.call !== 'function') {
    console.warn(
      '[ticketLoaders] reloadTicketsForRoot: no element to read through; board unchanged'
    )
    return
  }
  const filters = filtersFromRoot(root)
  const query = JSON.stringify(filters)
  const stillAsked = () => JSON.stringify(filtersFromRoot(root)) === query
  const colKeys = (root.columns || []).map((c) => c.key)
  const heldKeys = Object.keys(root.loadedByColumn || {})
  const totals = await fetchColumnCounts(el, filters, colKeys.length ? colKeys : null)
  if (!stillAsked()) return
  root.update({ totalByColumn: totals, tickets: [], loadedByColumn: {} })
  const keys = [...new Set([...Object.keys(totals), ...DEFAULT_COLUMN_KEYS, ...heldKeys])]
  const first = await Promise.all(
    keys.map(async (colKey) => {
      if (!totals[colKey]) return { colKey, rows: [] }
      // fetchColumnPage already returns _mongoToTicket-normalized rows
      const rows = await fetchColumnPage(el, colKey, 0, INITIAL_PAGE_SIZE, filters, 'reload')
      return { colKey, rows }
    })
  )
  if (!stillAsked()) return
  const merged = []
  const loaded = {}
  for (const { colKey, rows } of first) {
    merged.push(...rows)
    loaded[colKey] = rows.length
  }
  root.update({ tickets: merged, loadedByColumn: loaded })
}

// `this` is the calling element (el.call binds it): the reads go through
// it, the way pageColumn's do.
export const reloadTickets = async function () {
  return reloadTicketsForRoot(this.state?.root, this)
}
